import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const PackingListReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    lot: "",
    orderNum: "",
  });

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
    }

    if (filters.lot) {
      params.append('lot', filters.lot);
    }

    if (filters.orderNum) {
      params.append('order_num', filters.orderNum);
    }
    
    const queryString = params.toString();
    
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/packing-list-report${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data?.data?.length > 0) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        if (exportType === 'excel') {
          const excelData = [];
          
          response.data.data.forEach(order => {
            order.items.forEach(item => {
              item.sizes.forEach(size => {
                excelData.push({
                  "Merchant": order.merchant.name,
                  "Customer": order.customer.name,
                  "Party": order.party.name,
                  "Lot": order.lot,
                  "Container": order.container,
                  "Ex Factory": dayjs(order.ex_factory).format("YYYY-MM-DD"),
                  "Ship Date": dayjs(order.ship_date).format("YYYY-MM-DD"),
                  "Arrival": dayjs(order.arrival).format("YYYY-MM-DD"),
                  "Status": order.status,
                  "Production Style": item.p_style.name,
                  "Style": item.style.name,
                  "Item": item.item.name,
                  "Color": item.color,
                  "Size": size.size,
                  "Quantity": size.quantity,
                  "Total Pieces": item.total_pieces,
                  "Per Box": item.per_box,
                  "Total Boxes": item.total_box
                });
              });
            });
          });
          
          exportToExcel(excelData, `Packing_List_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Packing List Report</title>
            <style>
             body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1,h2 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .order-section { margin-bottom: 30px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Packing List Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
                ${filters.lot ? `<p><strong>Lot:</strong> ${filters.lot}</p>` : ''}
                <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
              
            </div>
        `;
        
        response.data.data.forEach((order, index) => {
          htmlContent += `
            <div class="order-section">
              <h2>Order #${index + 1}</h2>
              <table>
                <tr>
                  <td><strong>Merchant:</strong></td>
                  <td>${order.merchant.name}</td>
                  <td><strong>Customer:</strong></td>
                  <td>${order.customer.name}</td>
                  <td><strong>Party:</strong></td>
                  <td>${order.party.name}</td>
                </tr>
                <tr>
                  <td><strong>Lot:</strong></td>
                  <td>${order.lot}</td>
                  <td><strong>Container:</strong></td>
                  <td>${order.container}</td>
                  <td><strong>Status:</strong></td>
                  <td>${order.status}</td>
                </tr>
                <tr>
                  <td><strong>Ex Factory:</strong></td>
                  <td>${dayjs(order.ex_factory).format("YYYY-MM-DD")}</td>
                  <td><strong>Ship Date:</strong></td>
                  <td>${dayjs(order.ship_date).format("YYYY-MM-DD")}</td>
                  <td><strong>Arrival:</strong></td>
                  <td>${dayjs(order.arrival).format("YYYY-MM-DD")}</td>
                </tr>
              </table>

              <table style="margin-top: 15px;">
                <thead>
                  <tr>
                    <th>Production Style</th>
                    <th>Style</th>
                    <th>Item</th>
                    <th>Color</th>
                    <th>Size</th>
                    <th>Quantity</th>
                    <th>Total Pieces</th>
                    <th>Per Box</th>
                    <th>Total Boxes</th>
                  </tr>
                </thead>
                <tbody>
          `;

          order.items.forEach(item => {
            item.sizes.forEach((size, sizeIndex) => {
              htmlContent += `
                <tr>
                  <td>${item.p_style.name}</td>
                  <td>${item.style.name}</td>
                  <td>${item.item.name}</td>
                  <td>${item.color}</td>
                  <td>${size.size}</td>
                  <td>${size.quantity}</td>
                  <td>${sizeIndex === 0 ? item.total_pieces : ''}</td>
                  <td>${sizeIndex === 0 ? item.per_box : ''}</td>
                  <td>${sizeIndex === 0 ? item.total_box : ''}</td>
                </tr>
              `;
            });
          });

          htmlContent += `
                </tbody>
              </table>
            </div>
          `;
        });
        
        htmlContent += `
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Packing_List_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating packing list report:", error);
      toast.error("Failed to generate packing list report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
      if (filters.dateTo) params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
      if (filters.lot) params.append('lot', filters.lot);
      if (filters.orderNum) params.append('order_num', filters.orderNum);
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/packing-list-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data?.data?.length > 0) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Packing List Report</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1,h2 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .order-section { margin-bottom: 30px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Packing List Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
                ${filters.lot ? `<p><strong>Lot:</strong> ${filters.lot}</p>` : ''}
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
        `;
        
        response.data.data.forEach((order, index) => {
          htmlContent += `
            <div class="order-section">
              <h2>Order #${index + 1}</h2>
              <table>
                <tr>
                  <td><strong>Merchant:</strong></td>
                  <td>${order.merchant.name}</td>
                  <td><strong>Customer:</strong></td>
                  <td>${order.customer.name}</td>
                  <td><strong>Party:</strong></td>
                  <td>${order.party.name}</td>
                </tr>
                <tr>
                  <td><strong>Lot:</strong></td>
                  <td>${order.lot}</td>
                  <td><strong>Container:</strong></td>
                  <td>${order.container}</td>
                  <td><strong>Status:</strong></td>
                  <td>${order.status}</td>
                </tr>
                <tr>
                  <td><strong>Ex Factory:</strong></td>
                  <td>${dayjs(order.ex_factory).format("YYYY-MM-DD")}</td>
                  <td><strong>Ship Date:</strong></td>
                  <td>${dayjs(order.ship_date).format("YYYY-MM-DD")}</td>
                  <td><strong>Arrival:</strong></td>
                  <td>${dayjs(order.arrival).format("YYYY-MM-DD")}</td>
                </tr>
              </table>

              <table style="margin-top: 15px;">
                <thead>
                  <tr>
                    <th>Production Style</th>
                    <th>Style</th>
                    <th>Item</th>
                    <th>Color</th>
                    <th>Size</th>
                    <th>Quantity</th>
                    <th>Total Pieces</th>
                    <th>Per Box</th>
                    <th>Total Boxes</th>
                  </tr>
                </thead>
                <tbody>
          `;

          order.items.forEach(item => {
            item.sizes.forEach((size, sizeIndex) => {
              htmlContent += `
                <tr>
                  <td>${item.p_style.name}</td>
                  <td>${item.style.name}</td>
                  <td>${item.item.name}</td>
                  <td>${item.color}</td>
                  <td>${size.size}</td>
                  <td>${size.quantity}</td>
                  <td>${sizeIndex === 0 ? item.total_pieces : ''}</td>
                  <td>${sizeIndex === 0 ? item.per_box : ''}</td>
                  <td>${sizeIndex === 0 ? item.total_box : ''}</td>
                </tr>
              `;
            });
          });

          htmlContent += `
                </tbody>
              </table>
            </div>
          `;
        });
        
        htmlContent += `
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Packing_List_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="From Date"
          value={filters.dateFrom}
          onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="To Date"
          value={filters.dateTo}
          onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <TextField
          fullWidth
          label="Lot Number"
          value={filters.lot}
          onChange={(e) => setFilters({ ...filters, lot: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <TextField
          fullWidth
          label="Order Number"
          value={filters.orderNum}
          onChange={(e) => setFilters({ ...filters, orderNum: e.target.value })}
        />
      </Grid>
      
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default PackingListReport;
